// Librairies
import { CommonModule } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import { Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { first } from 'rxjs/operators';

// Services
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { ApiService } from 'src/app/services/core/api.service';

// Interfaces
import { Project, PutProject, ServiceConfig, serviceConfigs } from 'src/app/interfaces/api/project';

// Classes
import { Pop } from 'src/app/classes/shared/pop';

// Components
import { ButtonComponent } from 'ui-library';

export interface ServiceEntry {
  key: string;
  icon: string;
  color: string;
  config: ServiceConfig;
}

const TEMPLATE_DB = '0378_template';

@Component({
  selector: 'app-service-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
    ButtonComponent,
  ],
  templateUrl: './service-config.component.html',
  styleUrl: './service-config.component.scss',
})

export class ServiceConfigComponent implements OnInit {
  // Services
  private readonly pop = inject(Pop);
  private readonly projectService = inject(ProjectService);
  private readonly apiService = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);

  // Variables
  templateDashboards: string[] = [];
  templateUnits: string[] = [];
  templateFlows: string[] = [];
  templateUnitGroups: Array<{ key: string; units: string[] }> = [];
  isLoadingTemplate: boolean = true;
  isSaving: boolean = false;

  private readonly colorPalette = [
    '#6c63ff', '#00b09b', '#ff6b6b', '#f39c12', '#3498db',
    '#9b59b6', '#e74c3c', '#1abc9c', '#2ecc71', '#e67e22',
    '#16a085', '#8e44ad', '#2980b9', '#27ae60', '#d35400',
    '#c0392b', '#ff6b9d', '#5f27cd', '#00d2d3', '#ff9f43',
    '#48dbfb', '#1dd1a1', '#f1c40f', '#fd79a8', '#a29bfe',
  ];

  private readonly knownIcons: Record<string, string> = {
    antico: 'preview',
    lean: 'analytics',
    safety: 'gpp_maybe',
    kyd: 'settings_remote',
    zone: 'map',
    iot: 'sensors',
    other: 'settings',
  };

  private readonly knownColors: Record<string, string> = {
    antico: '#6c63ff',
    lean: '#00b09b',
    safety: '#ff6b6b',
    kyd: '#f39c12',
    zone: '#3498db',
    iot: '#9b59b6',
    other: '#95a5a6',
  };

  readonly datavizTabOptions: Array<{ value: string; label: string }> = [
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'events', label: 'Timeline' },
    { value: 'forge', label: 'Viewer 3D' },
    { value: 'maps', label: 'Maps' },
    { value: 'planning', label: 'Plannings' },
    { value: 'risks', label: 'Gestion des risques' },
    { value: 'timelapse', label: 'Timelapse' },
    { value: 'stream', label: 'Visite HSE' },
  ];

  readonly settingTabOptions: Array<{ value: string; label: string }> = [
    { value: 'info', label: 'Informations' },
    { value: 'resource', label: 'Ressources' },
    { value: 'flow', label: 'Automatismes' },
    { value: 'models', label: 'Modèles' },
    { value: 'users', label: "Liste d'utilisateurs" },
    { value: 'privacy', label: 'Confidentialité' },
    { value: 'tag', label: 'Éditeur de tag' },
    { value: 'exportdb', label: 'Export DB' },
    { value: 'kyd', label: 'Paramètre KYD' },
  ];

  // Signals
  readonly templateProject: WritableSignal<Project | null> = signal(null);
  services: WritableSignal<ServiceEntry[]> = signal([]);

  private readonly initialSnapshot = signal('');
  readonly isDirty = computed(() => JSON.stringify(this.services()) !== this.initialSnapshot());

  // --------- Angular lifecycle --------- //

  ngOnInit(): void {
    this.loadTemplateProject();
  }

  private loadTemplateProject(): void {
    this.projectService.getProjects({ database: TEMPLATE_DB }).pipe(
      first(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (projects) => {
        const project = projects[0];

        if (!project) {
          this.isLoadingTemplate = false;
          this.pop.error('Projet template introuvable');
          return;
        }

        this.templateProject.set(project);
        this.initServices(project);
        this.loadTemplateData(project.id);
      },
      error: (err) => {
        console.error('❌ Erreur chargement projet template:', err);
        this.isLoadingTemplate = false;
      },
    });
  }

  private initServices(project: Project): void {
    const saved = project.serviceConfigs;
    const source = saved && Object.keys(saved).length > 0 ? saved : serviceConfigs;

    const entries: ServiceEntry[] = Object.entries(source).map(([key, config]) => {
      const deepConfig: ServiceConfig = structuredClone(config);

      if (!deepConfig.data?.length)
        deepConfig.data = [{ database: TEMPLATE_DB, dashboards: [], units: [], flows: [] }];
      else if (!deepConfig.data[0]!.flows)
        deepConfig.data[0]!.flows = [];

      return {
        key,
        icon: deepConfig.icon ?? this.knownIcons[key] ?? 'category',
        color: deepConfig.color ?? this.knownColors[key] ?? this.randomColor(),
        config: deepConfig,
      };
    });

    this.services.set(entries);
    this.initialSnapshot.set(JSON.stringify(entries));
  }

  // --------- Get data --------- //

  private loadTemplateData(projectId: string): void {
    const tenantHeaders = new HttpHeaders({ 'X-Project-Db': TEMPLATE_DB });

    forkJoin({
      dashboards: this.apiService.getCollection('dashboards', '', { projects: projectId }).pipe(first()),
      units: this.apiService.getCollection('units', '', {}, { headers: tenantHeaders }).pipe(first()),
      flows: this.apiService.getCollection('flows', 'last', {}, { headers: tenantHeaders }).pipe(first()),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ dashboards, units, flows }) => {
        this.templateDashboards = this.mapField(dashboards, 'title');

        this.templateUnits = this.mapField(units, 'name');
        this.templateUnitGroups = this.buildUnitGroups(this.templateUnits);

        const flowNodes: any[] = Array.isArray(flows)
          ? (flows as any[]).flatMap((f: any) => f.flows ?? [])
          : (flows as any)?.flows ?? [];

        this.templateFlows = flowNodes
          .filter((n: any) => n.type === 'tab' && n.label)
          .map((n: any) => n.label as string);

        this.isLoadingTemplate = false;
      },
      error: (err) => {
        console.error('❌ Erreur chargement données template:', err);
        this.isLoadingTemplate = false;
      },
    });
  }

  // --------- Helpers --------- //

  private mapField(items: Array<Record<string, any>> | undefined, field: string): string[] {
    return (items ?? []).map(item => item[field] ?? '').filter(Boolean);
  }

  private extractGroupKey(name: string): string | null {
    return name.split(/[\s/_\-,]+/).find(t => /^[A-Z][A-Z0-9]*$/.test(t)) ?? null;
  }

  private buildUnitGroups(units: string[]): Array<{ key: string; units: string[] }> {
    const groupMap: Record<string, string[]> = {};

    for (const unit of units) {
      const key = this.extractGroupKey(unit) ?? unit;
      (groupMap[key] ??= []).push(unit);
    }

    return Object.entries(groupMap).map(([key, groupUnits]) => ({ key, units: groupUnits }));
  }

  isGroupFullyActive(groupUnits: string[], selected: string[]): boolean {
    return groupUnits.every(u => selected.includes(u));
  }

  isGroupPartiallyActive(groupUnits: string[], selected: string[]): boolean {
    return groupUnits.some(u => selected.includes(u)) && !this.isGroupFullyActive(groupUnits, selected);
  }

  toggleGroup(groupUnits: string[], selected: string[]): void {
    if (this.isGroupFullyActive(groupUnits, selected)) {
      groupUnits.forEach(u => {
        const i = selected.indexOf(u);

        if (i > -1)
          selected.splice(i, 1);
      });
    } else {
      groupUnits.forEach(u => { if (!selected.includes(u)) selected.push(u); });
    }

    this.services.update(s => [...s]);
  }

  private randomColor(): string {
    return this.colorPalette[Math.floor(Math.random() * this.colorPalette.length)] ?? '#607d8b';
  }

  randomizeColor(entry: ServiceEntry): void {
    let color: string;
    do { color = this.randomColor(); } while (color === entry.color);
    entry.color = color;
    this.services.update(s => [...s]);
  }

  onServiceNameChange(entry: ServiceEntry, newKey: string): void {
    entry.config.projectType = newKey ? [newKey] : [];
    this.services.update(s => [...s]);
  }

  isTabActive(arr: string[], value: string): boolean {
    return arr.includes(value);
  }

  toggleTab(arr: string[], value: string): void {
    const i = arr.indexOf(value);
    if (i > -1) arr.splice(i, 1);
    else arr.push(value);
    this.services.update(s => [...s]);
  }

  getServiceData(entry: ServiceEntry) {
    return entry.config.data![0]!;
  }

  poke(): void {
    this.services.update(s => [...s]);
  }

  // ─── CRUD services ────────────────────────────────────────────────────────

  addService(): void {
    const key = `service-${this.services().length + 1}`;

    this.services.update(s => [...s, {
      key,
      icon: 'category',
      color: this.randomColor(),
      config: {
        name: '',
        projectType: [key],
        datavizTabs: [],
        settingTabs: [],
        predictions: [],
        data: [{ database: TEMPLATE_DB, dashboards: [], units: [], flows: [] }],
      },
    }]);
  }

  deleteService(index: number): void {
    const entry = this.services()[index];

    if (!entry) return;

    this.pop.confirm(`Supprimer le service "${entry.key}" ?`, (result: any) => {
      if (result.ok)
        this.services.update(s => s.filter((_, i) => i !== index));
    });
  }

  saveConfig(): void {
    if (!this.isDirty() || this.isSaving) return;

    const projectId = this.templateProject()?.id;
    if (!projectId) return;

    const configs: Record<string, ServiceConfig> = {};

    for (const entry of this.services()) {
      configs[entry.key] = { ...entry.config, icon: entry.icon, color: entry.color };
    }

    this.isSaving = true;

    const query: PutProject = {
      id: projectId,
      serviceConfigs: configs
    }

    this.projectService.setProject(query).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isSaving = false;
        this.initialSnapshot.set(JSON.stringify(this.services()));
        this.templateProject.update(p => ({ ...p!, serviceConfigs: configs }));
        this.pop.toast('Configuration sauvegardée', 'success', 4000, 'save');
      },
      error: (err) => {
        this.isSaving = false;
        console.error('Erreur sauvegarde config:', err);
        this.pop.error('Erreur lors de la sauvegarde');
      },
    });
  }

  trackByKey(_: number, entry: ServiceEntry): string {
    return entry.key;
  }
}
